/* eslint-disable no-useless-computed-key */
import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_VENDORS,
  RECEIVE_VENDOR,
  RECEIVE_ALL_VENDORS,
  RECEIVE_PARENT_VENDORS,
  RECEIVE_VENDOR_METADATA,
  RECEIVE_VENDOR_FAILURE,
  RECEIVING_VENDORS,
  SAVING_VENDOR,
  UPDATING_VENDOR,
  DELETE_VENDOR,
  DELETING_VENDOR,
  DELETING_VENDOR_FAILURE,
  RECEIVE_ADDRESSES,
  RESET_VENDOR_META,
  CLEAR_VENDOR_STATE,
  CLEAR_STATE,
  UPDATE_CURRENT_VENDOR_PAGE,
  RECEIVE_STATES,
  INCREMENT_VENDOR_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { getBySearchCriteria } from '@/utils/helpers/GetterHelpers/CommonGetterFunctions'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const addressSchema = new schema.Entity('addresses')
const stateSchema = new schema.Entity('states')
const vendorSchema = new schema.Entity('vendors', {
  address_billing: addressSchema,
  address_contact: addressSchema,
  address_mailing: addressSchema,
  address_selected: addressSchema,
  work_state: stateSchema
})
const vendorListSchema = new schema.Array(vendorSchema)

const state = {
  all: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null,
  autoCode: null
}

const actions = {
  fetchVendorsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_VENDORS, true)
    return Vue.http.get(buildUrlWithQuery('vendors', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_VENDORS, false)
        this.commit(RESET_VENDOR_META)
        if (!(r && r.results)) return Promise.reject('Error fetching vendors')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, vendorListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_VENDORS, { data: entities.vendors })
        this.commit(RECEIVE_VENDOR_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_VENDORS, false)
        return e
      })
  },
  fetchSingleVendorWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_VENDORS, true)
    return Vue.http.get(buildUrlWithQuery('vendors', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_VENDORS, false)
        if (!(r && r.results)) return this.commit(RECEIVE_VENDOR_FAILURE, 'Error fetching vendor')
        const { results } = r
        const { entities } = normalize(results, vendorListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_PARENT_VENDORS, { data: entities.vendors })
        return { query, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_VENDORS, false)
        return e
      })
  },
  fetchVendorById (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_VENDORS, true)
    return Vue.http.get(buildUrl(`vendors/${id}`, null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        this.commit(RECEIVING_VENDORS, false)
        if (!(response && response.id)) return this.commit(RECEIVE_VENDOR_FAILURE, 'Error fetching vendor')
        this.commit(RECEIVE_VENDOR, response)
        return response
      })
      .catch(e => {
        this.commit(RECEIVING_VENDORS, false)
        return e
      })
  },
  fetchAllVendors (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_VENDORS, true)
    return Vue.http({ url: buildUrl('vendors?meta_choices=True', null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        this.commit(RECEIVING_VENDORS, false)
        if (!response.hasOwnProperty('choices')) return this.commit(RECEIVE_VENDOR_FAILURE, 'Error fetching vendors')
        if (!response || !Array.isArray(response.choices)) return this.commit(RECEIVE_VENDOR_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_VENDORS, { data: response.choices })
        return response.choices
      })
      .catch(e => {
        this.commit(RECEIVING_VENDORS, false)
        return e
      })
  },
  /**
   * Fetches all vendor models. Allows for a custom query to be passed, which can, among other params, include a
   * specified page_size. If no page_size is passed, or if no query is passed, creates a query object with default
   * page_size of 100.
   * @param store
   * @param query
   * @param commitToStore
   * @return {Promise<void>}
   */
  async fetchAllVendorModels (store, payload = {}) {
    let query = {}
    query.page_size = (payload.query || {}).page_size || 100
    const commitToStore = payload.commitToStore || false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const makeRequest = currentQuery => {
        return Vue.http.get(buildUrlWithQuery('vendors', currentQuery, instanceUrl), { headers })
          .then(checkStatus)
          .then(parseJSON)
          .catch(e => console.log(e))
      }
      const resp = await makeRequest(query)
      if (!(resp && resp.count && resp.results)) {
        throw new Error('unable to fetch first page')
      }
      let ret = [ ...resp.results ]
      if (resp.count > query.page_size) { // Get additional pages, if applicable
        let requests = []
        for (let i = 2; i <= (Math.ceil(resp.count / resp.results.length)); i++) {
          requests.push(makeRequest({ ...query, page: i }))
        }
        const pages = await Promise.all(requests)
        if (!pages.every(page => page && page.hasOwnProperty('results') && Array.isArray(page.results))) {
          throw new Error('unable to fetch subsequent page results')
        }
        pages.forEach(page => (ret = [ ...ret, ...page.results ]))
      }
      // console.log(`${ret.length} vendors successfully fetched`)
      if (commitToStore) {
        this.commit(RECEIVE_ALL_VENDORS, { data: ret })
      }
      return ret
    } catch (e) {
      console.log(`Error fetching all vendors: ${e.message}`)
      return []
    }
  },
  saveVendor (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_VENDOR, true)
    return Vue.http.post(buildUrl('vendors', null, instanceUrl), payload, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(SAVING_VENDOR, false)
        if (!r) return Promise.reject('Error saving vendor')
        const { entities } = normalize(r, vendorSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.states) {
          this.commit(RECEIVE_STATES, { data: entities.addresses })
        }
        this.commit(RECEIVE_VENDORS, { data: entities.vendors })
        this.commit(INCREMENT_VENDOR_COUNT, 1)
        return { response: r, entities }
      })
      .catch(e => {
        this.commit(SAVING_VENDOR, false)
        return e
      })
  },
  updateVendor (store, payload) {
    if (!payload) return Promise.reject('Error updating vendor')
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_VENDOR, id)
    if (data.address_billing) data.address_billing.id = undefined
    if (data.address_contact) data.address_contact.id = undefined
    if (data.address_mailing) data.address_mailing.id = undefined
    return Vue.http.patch(buildUrl(`vendors/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_VENDOR, null)
        if (r && r.status === 200) {
          const { entities } = normalize(r.body, vendorSchema)
          if (entities && entities.addresses) {
            this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
          }
          if (entities && entities.states) {
            this.commit(RECEIVE_STATES, { data: entities.addresses })
          }
          this.commit(RECEIVE_VENDORS, { data: entities.vendors })
          return { response: r.body, entities }
        } else {
          return Promise.reject('Failed to update vendor')
        }
      })
      .catch(e => {
        this.commit(UPDATING_VENDOR, null)
        return e
      })
  },
  deleteVendor (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (payload) {
      const { id } = payload
      this.commit(DELETING_VENDOR, true)
      this.commit(DELETING_VENDOR_FAILURE, { id: '', err: '' })
      return Vue.http.delete(buildUrl(`vendors/${id}`, null, instanceUrl), { headers })
        .then(checkStatus)
        .then(() => {
          this.commit(DELETE_VENDOR, payload)
          this.commit(DELETING_VENDOR, false)
          return payload
        })
        .catch(e => {
          console.log(e)
          this.commit(DELETING_VENDOR_FAILURE, { id: payload, err: e || 'Error deleting vendor' })
        })
    } else {
      this.commit(DELETING_VENDOR, false)
    }
  },
  /**
   * Check vendor has transactions
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async vendorHasTransactions (store, payload) {
    const { id } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl(`vendors/${id}/has_transactions`, null, instanceUrl), { headers })
      .then(r => {
        if (r && r.status === 200) {
          return r.body
        }
      })
      .catch(e => {
        console.log(e)
      })
  },
  fetchVendorAutoCode (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl(`vendors/autocode`, query, instanceUrl), { headers })
      .then(r => {
        if (!(r && r.status === 200 && r.body.code)) return Promise.reject()
        this.commit('RECEIVE_VENDOR_AUTOCODE', r.body.code)
        return r
      })
      .catch(e => {
        console.log(e)
      })
  },
  clearVendorState (store, clearCache = false) {
    this.commit(CLEAR_VENDOR_STATE, clearCache)
  },
  resetVendorMeta () {
    return this.commit(RESET_VENDOR_META)
  }
}

const getters = {
  getVendorsCache: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getVendorMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getVendorById: (state) => (id) => {
    if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllVendors: (state) => {
    return state.all
  },
  getVendorFetchStatus: (state) => {
    return state.receivingData
  },
  getVendorsBySearchCriteria: (state) => (query) => {
    return getBySearchCriteria(state.cache, query)
  },
  getVendorSaveStatus: (state) => {
    return state.saving
  },
  getVendorUpdateStatus: (state) => {
    return state.updating
  },
  getVendorDeleteStatus: (state) => {
    return state.deleting
  },
  getCurrentVendorPage: (state) => {
    if (state.currentPage) return state.currentPage
  },
  getVendorAutoCode: (state) => state.autoCode
}

const mutations = {
  [RECEIVE_VENDORS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVE_PARENT_VENDORS] (state, result) {
    if (!(result && Object.keys(result).length)) return
    let { data } = result
    for (let i in data) {
      if (state.cache[i]) {
        data[i] = Object.assign({}, state.cache[i], data[i])
      } else {
        data[i]['page'] = Math.ceil(state.count / 20)
      }
    }
    state.cache = Object.assign({}, state.cache, data)
  },
  [RECEIVE_VENDOR] (state, data) {
    const id = data.id
    if (!state.data) state.data = {}
    state.data[id] = data
    state.cache[id] = data
    return true
  },
  [RECEIVE_ALL_VENDORS] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVING_VENDORS] (state, payload) {
    state.receivingData = payload
  },
  [SAVING_VENDOR] (state, payload) {
    state.saving = payload
  },
  [UPDATING_VENDOR] (state, payload) {
    state.updating = payload
  },
  [DELETE_VENDOR] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_VENDOR] (state, payload) {
    state.deleting = payload
  },
  [DELETING_VENDOR_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_VENDOR_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_VENDOR_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [UPDATE_CURRENT_VENDOR_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_VENDOR_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_VENDOR_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  ['RECEIVE_VENDOR_AUTOCODE'] (state, payload) {
    state.autoCode = payload
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/vendor.js